import { useDraggable } from '@dnd-kit/core';
import { useAtomValue } from 'jotai';
import React from 'react';
import clsx from 'clsx';
import { hideTooltipInfoAtom } from '../atoms';
import styles from './DraggableModules.module.scss';
import type { ServerModule } from '../ServerWorkflowType';
import { useModules } from './useModules';
import { ModuleSearch } from './ModuleSearch/ModuleSearch';
import { Tooltip } from '@wk/components-v3-react16';

interface DraggableModuleProperties {
  id?: string;
  module: ServerModule;
}

export const Module = ({ module, id }: DraggableModuleProperties) => {
  const disabledTooltip = useAtomValue(hideTooltipInfoAtom);
  return (
    <>
      <span className={styles.sixDots} />
      <div className={styles.moduleContent} id={id}>
        <div className={styles.moduleName}>{module.name}</div>
        <div className={styles.moduleVersion}>{module.version}</div>
      </div>
      {!disabledTooltip && (
        <Tooltip
          mode="description"
          targetSelector={`#${id}`}
          elementId="description"
        >
          Select this option to explore further customization settings
        </Tooltip>
      )}
    </>
  );
};

export function DraggableModule({ module }: DraggableModuleProperties) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    data: module,
    id: `module-${module.id}-${module.name}-${module.version}`,
  });

  return (
    <div
      ref={setNodeRef}
      className={clsx(styles.draggableModule, {
        [styles.dragging]: isDragging,
        [styles.notDragging]: !isDragging,
      })}
      {...listeners}
      {...attributes}
    >
      <Module module={module} />
    </div>
  );
}

export const DraggableModules = () => {
  const { systemModules, nonSystemModules, isLoading } = useModules();

  if (isLoading) {
    return (
      <div className={styles.modulesList}>
        <h4>Modules</h4>
        <div className={styles.modulesContainer}>
          <div>Loading modules...</div>
        </div>
      </div>
    );
  }

  const hasNoModules =
    systemModules.length === 0 && nonSystemModules.length === 0;

  return (
    <div className={styles.modulesList}>
      <ModuleSearch />
      <div className={styles.modulesContainer}>
        {hasNoModules ? (
          <div className={styles.emptyState}>
            No modules found matching your search.
          </div>
        ) : (
          <>
            {nonSystemModules.length > 0 && (
              <div className={styles.moduleSection}>
                <h4 className={styles.sectionTitle}>
                  Custom Modules ({nonSystemModules.length})
                </h4>
                {nonSystemModules.map((module) => (
                  <DraggableModule
                    key={`${module.id}-${module.version}`}
                    module={module}
                  />
                ))}
              </div>
            )}
            {systemModules.length > 0 && (
              <div className={styles.moduleSection}>
                <h4 className={styles.sectionTitle}>
                  System Modules ({systemModules.length})
                </h4>
                {systemModules.map((module) => (
                  <DraggableModule
                    key={`${module.id}-${module.version}`}
                    module={module}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DraggableModules;
